
import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCcw, Plus, Upload } from "lucide-react";
import { useUserManagement } from "./user-management/hooks/useUserManagement";
import { useUserForm } from "./user-management/hooks/useUserForm";
import { UserFormDialog } from "./user-management/UserFormDialog";
import { UserTable } from "./user-management/UserTable";
import { UserDialogs } from "./user-management/UserDialogs";
import AddCompanyDialog from "./AddCompanyDialog";
import { ImportUsersDialog } from "./user-management/ImportUsersDialog";

const UserManagementTab = React.memo(() => {
  const {
    profiles,
    loading,
    companies,
    supervisors,
    fetchProfiles,
    fetchCompanies,
    fetchSupervisors,
    handleCompanyAdded,
    handleLoginToggle,
    getSupervisorName
  } = useUserManagement();

  const {
    form,
    dialogOpen,
    setDialogOpen,
    currentUser,
    isEditing,
    showPassword,
    setShowPassword,
    errorDialogOpen,
    setErrorDialogOpen,
    errorMessage,
    deleteDialogOpen,
    setDeleteDialogOpen,
    addCompanyDialogOpen,
    setAddCompanyDialogOpen,
    watchedRole,
    watchedCompany,
    handleAddUser,
    handleEditUser,
    handleDeleteUser,
    confirmDeleteUser,
    onSubmit,
    handleCompanySelectChange,
    importDialogOpen,
    setImportDialogOpen,
    handleImportUsers,
    resetPasswordLoading,
    handleResetPassword,
    successDialogOpen,
    setSuccessDialogOpen,
    successMessage
  } = useUserForm(fetchProfiles, fetchCompanies, fetchSupervisors);

  useEffect(() => {
    fetchSupervisors(watchedRole, watchedCompany);
  }, [watchedRole, watchedCompany]);

  const handleCompanyAdd = (companyName: string) => {
    handleCompanyAdded(companyName);
    form.setValue("company_name", companyName);
  };

  return (
    <div className="mt-8 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-white">User Management</h2>
        <div className="flex gap-2">
          <Button className="bg-primary hover:bg-primary/90 text-black" onClick={fetchProfiles}>
            Refresh
          </Button>
          <Button className="bg-primary hover:bg-primary/90 text-black" onClick={() => setImportDialogOpen(true)}>
            Import Users
          </Button>
          <Button className="bg-primary hover:bg-primary/90 text-black" onClick={handleAddUser}>
            Add User
          </Button>
        </div>
      </div>

      <UserFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        form={form}
        onSubmit={onSubmit}
        isEditing={isEditing}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        companies={companies}
        supervisors={supervisors}
        watchedRole={watchedRole}
        handleCompanySelectChange={handleCompanySelectChange}
        onResetPassword={handleResetPassword}
        resetPasswordLoading={resetPasswordLoading}
      />

      <AddCompanyDialog
        open={addCompanyDialogOpen}
        onOpenChange={setAddCompanyDialogOpen}
        onCompanyAdded={handleCompanyAdd}
      />

      <ImportUsersDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImportSuccess={handleImportUsers}
      />

      <UserDialogs
        errorDialogOpen={errorDialogOpen}
        setErrorDialogOpen={setErrorDialogOpen}
        errorMessage={errorMessage}
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        confirmDeleteUser={confirmDeleteUser}
        successDialogOpen={successDialogOpen}
        setSuccessDialogOpen={setSuccessDialogOpen}
        successMessage={successMessage}
      />

      <UserTable
        profiles={profiles}
        loading={loading}
        onEditUser={handleEditUser}
        onDeleteUser={handleDeleteUser}
        onLoginToggle={handleLoginToggle}
        getSupervisorName={getSupervisorName}
      />
    </div>
  );
});

UserManagementTab.displayName = "UserManagementTab";

export default UserManagementTab;
